import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom';
import { formatDateTODMYHMA, getReadableErrorMessage, isPastDate } from '../../../utils/utils';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import MediaCarousel from '../comp/MediaCarousel';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar as fullStar } from '@fortawesome/free-solid-svg-icons'
import { faStarHalfAlt, faStar as emptyStar } from '@fortawesome/free-regular-svg-icons'
import { CustomLoader } from '../../common/CustomLoader';


export default function ServiceDetails() {
  const location = useLocation();
  const service = location.state?.service;


  const media = [
    ...(service?.images || []).map((img) => ({ type: "image", url: img })),
    ...(service?.videos || []).map((vid) => ({ type: "video", url: vid })),
  ];

  // Per review Only one reply is Allowed By Seller
  const [isReviewsLoading, setIsReviewsLoading] = useState(false);
  const [error, setError] = useState("");

  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    // Last 5 reviews
    const getServiceReviews = async () => {
      setIsReviewsLoading(true);
      try {
        const reviewsRef = collection(db, "serviceReviews");
        let queryConstraints = [];
        queryConstraints.push(orderBy("createdAt", "desc"));
        queryConstraints.push(where("serviceId", '==', service.id));
        const reviewsQuery = query(reviewsRef, ...queryConstraints);
        const reviewSnap = await getDocs(reviewsQuery);
        const fetchedReviews = [];
        for (const doc of reviewSnap.docs) {
          fetchedReviews.push(doc.data());
        }
        setReviews(fetchedReviews);
        setIsReviewsLoading(false);
        setError("");
      }
      catch (e) {
        let errorsMsg = getReadableErrorMessage(e.code || e.message);
        setError(errorsMsg);
        setIsReviewsLoading(false);
      }
      finally {
        setIsReviewsLoading(false);
      }
    };

    if (service !== null) {
      getServiceReviews();
    }
  },
    [service]);

  if (!service) {
    return <div>
      <p>No Service data found!</p>
    </div>;
  }

  return (
    <div className="mx-auto">

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Media: Images and Video Carousel */}
        <div className="w-full lg:w-1/2">
          <MediaCarousel media={media} />
        </div>

        {/* Other Details */}
        <div className="w-full lg:w-1/2 text-medium-text-color">
          <h2 className="text-xl font-semibold mb-4">Service Details</h2>
          {/* Name */}
          <div className='mb-2 text-lg'>
            Name: <span className="font-semibold">{service.name}</span> ({service.id.split("-")[1]})
          </div>
          {/* Device Type */}
          <div className='mb-2 text-lg'>
            Device Type: <span className="font-semibold">{service.deviceType}</span>
          </div>


          {/* OS Type */}
          <div className='mb-2 text-lg'>
            OS Type: <span className="font-semibold">{service.osType}</span>
          </div>


          {/* Brand Name */}
          <div className='mb-2 text-lg'>
            Brand Name: <span className="font-semibold">{service.brandName}</span>
          </div>


          {/* Model Name */}
          <div className='mb-2 text-lg'>
            Model Name: <span className="font-semibold">{service.modelName}</span>
          </div>


          {/* Price */}
          <div className="mb-2 text-lg flex items-center gap-2">
            <span>Price:</span>
            {isPastDate(service.timeUntilDiscountExpire) === true ? (
              <span className="font-semibold text-lg">PKR {service.price}</span>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-base line-through text-light-text-color">PKR {service.price}</span>
                <span className="text-lg font-semibold text-bright-green">PKR {service.discountPrice}</span>
              </div>
            )}
          </div>








        </div>
      </div>


      <div className="mt-4 text-medium-text-color">
        {/* Description */}
        <div className='mb-2 text-base'>
          <h2 className="text-xl font-semibold mb-2">Description</h2>
          {service.description}
        </div>
        {/* Keywords */}
        <div className='mb-2 text-base'>
          <h2 className="text-xl font-semibold mb-2">Keywords</h2>
          {service.keywords.join(", ")}
        </div>

        {/* Reviews */}
        <div className='mb-2'>
          {/* Reviews */}
          <div className='mb-2'>
            <div className='text-base flex items-center gap-2'>
              <h2 className="text-xl font-semibold">Reviews</h2>
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }, (_, i) => {
                  const ratingValue = i + 1;
                  return (
                    <span key={i}>
                      {service.ratings >= ratingValue ? (
                        <FontAwesomeIcon icon={fullStar} color='orange' />
                      ) : service.ratings >= ratingValue - 0.5 ? (
                        <FontAwesomeIcon icon={faStarHalfAlt} color='orange' />
                      ) : (
                        <FontAwesomeIcon icon={emptyStar} color='orange' />
                      )}
                    </span>
                  );
                })}
                <span className="ml-2 text-sm text-gray-600">
                  {service.ratings} ({service.ratingsCount ?? 0})
                </span>
              </div>
            </div>

            {/* Reviews */}
            <div className='mb-4 mt-4 flex flex-1 justify-center items-center'>
              {isReviewsLoading ? (
                <CustomLoader />
              ) : error.length > 0 ? (
                <p className="text-error-color text-sm mt-1 mb-1 mx-3" style={{ whiteSpace: 'pre-line' }}>{error}</p>
              ) : reviews.length === 0 ? (
                <p className='font-medium text-light-text-color'>No Reviews found.</p>
              ) : (
                <div className="flex-1 overflow-auto">
                  {reviews.map((review, index) => (
                    <div key={index} className="flex justify-between p-4 border-b">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{review.userName}</h3>
                          <p>
                            <span key={review.serviceId} className="text-yellow-500">
                              {Array.from({ length: 5 }, (_, i) => {
                                const ratingValue = i + 1;
                                return (
                                  <span key={i}>
                                    {review.ratings >= ratingValue ? (
                                      <FontAwesomeIcon icon={fullStar} color="orange" />
                                    ) : review.ratings >= ratingValue - 0.5 ? (
                                      <FontAwesomeIcon icon={faStarHalfAlt} color="orange" />
                                    ) : (
                                      <FontAwesomeIcon icon={emptyStar} color="orange" />
                                    )}
                                  </span>
                                );
                              })}
                              <span className="ml-2 text-sm text-gray-600">
                                ({review.ratings})
                              </span>
                            </span>
                          </p>
                        </div>
                        <p className="text-gray-600">{review.orderFeedback}</p>

                        {/* Seller reply section */}
                        {review.hasSellerReply ? (
                          <div className="mt-4 p-4 border-t">
                            <h4 className="font-semibold">Seller's Reply:</h4>
                            <p className="text-gray-600">{review.sellerReply}</p>
                            <p className="text-gray-500 text-sm">Replied on {formatDateTODMYHMA(review.sellerReplyAt)}</p>
                          </div>
                        ) : (
                          <></>
                        )}
                      </div>
                      <div className="text-gray-500 text-sm">
                        {formatDateTODMYHMA(review.createdAt)}
                      </div>
                    </div>
                  ))}
                </div>

              )}
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}
